import { useState, useEffect, useCallback } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@repo/ui";
import {
  getBillingInfo,
  getInvoices,
  getPlanPrices,
  type BillingInfo,
  type InvoiceItem,
  type PlanPrices,
} from "@/lib/api/stripe";
import { BillingPlanCard } from "./billing-plan-card";
import { BillingInvoicesCard } from "./billing-invoices-card";
import { BillingSupportCard } from "./billing-support-card";

interface PharmacyData {
  subscription_status: string;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
}

interface SubscriptionSectionProps {
  pharmacy: PharmacyData;
}

export function SubscriptionSection({ pharmacy }: SubscriptionSectionProps) {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [prices, setPrices] = useState<PlanPrices | null>(null);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(true);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    try {
      setIsBillingLoading(true);
      const [billingData, pricesData] = await Promise.all([
        pharmacy.stripe_customer_id ? getBillingInfo() : Promise.resolve(null),
        getPlanPrices(),
      ]);
      setBilling(billingData);
      setPrices(pricesData);
    } catch {
      setError("Failed to load billing information");
    } finally {
      setIsBillingLoading(false);
    }
  }, [pharmacy.stripe_customer_id]);

  const fetchInvoices = useCallback(async () => {
    if (!pharmacy.stripe_customer_id) {
      setIsInvoicesLoading(false);
      return;
    }

    try {
      setIsInvoicesLoading(true);
      const data = await getInvoices({ limit: 10 });
      setInvoices(data.invoices);
      setHasMoreInvoices(data.hasMore);
    } catch {
      // Non-critical — don't show error for invoices
    } finally {
      setIsInvoicesLoading(false);
    }
  }, [pharmacy.stripe_customer_id]);

  const refreshBilling = useCallback(() => {
    fetchBilling();
    fetchInvoices();
  }, [fetchBilling, fetchInvoices]);

  useEffect(() => {
    fetchBilling();
    fetchInvoices();
  }, [fetchBilling, fetchInvoices]);

  const handleLoadMore = async () => {
    const lastInvoice = invoices[invoices.length - 1];
    if (!lastInvoice) return;

    setIsLoadingMore(true);
    try {
      const data = await getInvoices({
        limit: 10,
        starting_after: lastInvoice.id,
      });
      setInvoices((prev) => [...prev, ...data.invoices]);
      setHasMoreInvoices(data.hasMore);
    } catch {
      // Silently fail — user can retry
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/50 p-6 text-center space-y-3">
        <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
        <p className="text-sm font-medium text-destructive">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setError(null);
            fetchBilling();
            fetchInvoices();
          }}
        >
          <Loader2 className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BillingPlanCard
        billing={billing}
        pharmacy={pharmacy}
        prices={prices}
        paymentMethod={billing?.paymentMethod ?? null}
        hasStripeCustomer={!!pharmacy.stripe_customer_id}
        isLoading={isBillingLoading}
        onSubscriptionChange={refreshBilling}
      />

      <BillingInvoicesCard
        invoices={invoices}
        hasMore={hasMoreInvoices}
        isLoading={isInvoicesLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <BillingSupportCard />
    </div>
  );
}
